import React, { useState, useEffect } from 'react';

// Hoisted so its identity is stable across renders — that both silences the
// exhaustive-deps warning and means the word-advance effect isn't torn down
// and rebuilt on every tick.
const WORDS = ['Remembrance', 'Renewal', 'Respect'];

const SplashScreen = ({ fadeOut }) => {
  const [showWords, setShowWords] = useState(false);
  const [currentWordIndex, setCurrentWordIndex] = useState(0);

  useEffect(() => {
    const wordTimer = setTimeout(() => setShowWords(true), 1000);
    return () => clearTimeout(wordTimer);
  }, []);

  useEffect(() => {
    if (showWords && currentWordIndex < WORDS.length - 1) {
      const timer = setTimeout(() => {
        setCurrentWordIndex((prev) => prev + 1);
      }, 1500);
      return () => clearTimeout(timer);
    }
    return undefined;
  }, [showWords, currentWordIndex]);

  // <main>, not <div>: while the splash is up it replaces the entire app tree,
  // so a plain div leaves every visible element outside any landmark. The two
  // branches are mutually exclusive, so this never doubles App's own <main>.
  return (
    <main
      className={`fixed inset-0 flex flex-col items-center justify-center overflow-hidden bg-white transition-opacity duration-1000 ${
        fadeOut ? 'opacity-0' : 'opacity-100'
      }`}
      style={{ zIndex: 9999 }}
    >
      {/* Soft navy glow so the logo sits in a pool of brand colour rather than
          on flat white. */}
      <div
        className="pointer-events-none absolute inset-0"
        style={{
          background:
            'radial-gradient(60% 55% at 50% 42%, rgba(0,57,143,0.10) 0%, rgba(8,45,104,0.04) 42%, rgba(255,255,255,0) 72%)',
        }}
      />

      <div className="relative flex flex-col items-center animate-fade-in">
        <img
          src="/images/navbar/logo.png"
          alt="Cemetery & Burial Solutions"
          className="h-52 drop-shadow-[0_18px_45px_rgba(8,45,104,0.20)] md:h-64"
        />

        {/* Cycling words — now in the site's Cormorant serif and brand navy, so
            the splash reads as the same brand as everything after it. */}
        <div className="relative mt-4 flex h-16 w-80 flex-col items-center justify-center overflow-hidden">
          {WORDS.map((word, index) => (
            <div
              key={word}
              className={`absolute w-full text-center transition-all duration-1000 ease-in-out ${
                index === currentWordIndex
                  ? 'translate-y-0 opacity-100'
                  : index < currentWordIndex
                    ? '-translate-y-full opacity-0'
                    : 'translate-y-full opacity-0'
              }`}
            >
              <span className="font-cormorant text-4xl font-medium tracking-[0.08em] text-navy-800 md:text-5xl">
                {word}
              </span>
            </div>
          ))}
        </div>

        {/* Progress dots that fill as the words advance. */}
        <div className="mt-5 flex items-center gap-2" aria-hidden="true">
          {WORDS.map((word, index) => (
            <span
              key={word}
              className={`h-1.5 rounded-full transition-all duration-500 ${
                index <= currentWordIndex ? 'w-6 bg-navy-700' : 'w-1.5 bg-navy-200'
              }`}
            />
          ))}
        </div>
      </div>
    </main>
  );
};

export default SplashScreen;
